import Axios from 'axios';
import store from '@/store';
import router from '@/router/index';

import { notification } from '@/utils/notification';
import routeName from '@/router/constant.js';
import { isProdMode } from '@/utils/is';
import { requestOptions } from './requestOptions';

const pending = new Map();
// const isProdMode = process.env.MODE === 'prod';
/**
 * 添加請求
 * @param {Object} config
 */
const addPending = (config) => {
    const url = [
        config.method,
        config.url,
        JSON.stringify(config.params),
        JSON.stringify(config.data),
    ].join('&');
    config.cancelToken = config.cancelToken || new Axios.CancelToken(cancel => {
        if (!pending.has(url)) { // 如果 pending 中不存在當前請求，則添加
            pending.set(url, cancel);
        }
    });
};
/**
* 移除請求
* @param {Object} config
*/
const removePending = (config) => {
    const url = [
        config.method,
        config.url,
        JSON.stringify(config.params),
        JSON.stringify(config.data),
    ].join('&');
    if (pending.has(url)) { // 如果在 pending 中存在當前請求標誌，需要取消當前請求，並移除
        const cancel = pending.get(url);
        cancel(url);
        pending.delete(url);
    }
};
/**
 * 清空 pending 中的請求（在路由调用）
 */
export const clearPending = () => {
    for (const [url, cancel] of pending) {
        cancel(url);
    }
    pending.clear();
};
/**
 * 異常處理
 */
const httpErrorStatusHandle = (error, apiRoutePage) => {
    if (Axios.isCancel(error)) return console.error('重複請求：' + error.message);
    let message = '';
    if (error && error.response) {
        switch (error.response.status) {
            case 302: message = '接口重定向了！'; break;
            case 400: message = error.data?.errorMessage || '請確認資料再試一次'; break;
            case 401: message = '請重新登入線上交易專區'; break;
            case 403: message = '請先登入取得權限'; break;
            case 404: message = `請確認網址： 
            ${!isProdMode() ? error.response.config.url : ''}`; break;
            case 408: message = '連線逾時，請稍後再試'; break;
            case 409: message = '系统已存在相同數據！'; break;
            case 500: message = '系統忙碌中，請稍後再試'; break;
            case 501: message = '系統忙碌中，請稍後再試'; break;
            case 502: message = '系統忙碌中，請稍後再試'; break;
            case 503: message = '我們正在提供更好的服務，請稍後再試'; break;
            case 504: message = '我們正在提供更好的服務，請稍後再試'; break;
            case 505: message = 'HTTP版本不受支持！'; break;
            default: message = '系統忙碌中，請稍後再試！'; break;
        }
    }

    if (error.message && error.message.includes('timeout')) message = '連線逾時，請稍後再試'; // OK
    if (error.message && error.message.includes('Network')) message = window.navigator.onLine ? '服務端異常！' : '請確認網路連線！';
    if (typeof error === 'string') {
        if (error.includes('查詢序號')) {
            let ary = error.split('查詢序號：');
            ary = ary[1].split('</P>');
            message = `請將以下查詢序號提供給您的IT窗口：${ary[0]}`;
        }
    }

    if (message === '') {
        message = error.data?.errorMessage || '系統忙碌中，請稍後再試';
    }
    const title = error.response?.status || error.data?.errorCode || '站台公告';
    if (title === 401 || title === 'System.Unauthorized') {
        store.dispatch('permissions/clearAll', { page: routeName.NO_AUTH });
    } else {
        const currentRoutePage = router?.currentRoute._value.name;
        // 換頁不顯示
        if (currentRoutePage === apiRoutePage) {
            notification({ title, text: message });
        }
    }
};
/**
 * @param {String} url 請求地址
 * @param {Any} params 請求参數
 * @param {Object} options本次請求配置；
 * @param {Boolean} errorDialog - 本次是否顯示錯誤提示（僅針對data.detail顯示）
 * @param {Boolean} closeAlert - 是否顯示錯誤提示(所有錯誤訊息)
 * @param {Boolean} formData - 檔案格式
 * @param {Boolean} baseURL - 是否為基礎環境
 * @param {Boolean} repeatRequestCancel - 開啟禁止重複打相同api
 */

function request(url, params, options = requestOptions, method) {
    const { closeAlert, errorDialog, baseURL, repeatRequestCancel, responseType, formData } = options;
    // 當前請求的頁面名稱
    const apiRoutePage = router?.currentRoute._value.name;
    const contentType = formData ? 'application/x-www-form-urlencoded; charset=UTF-8' : 'application/json; charset=UTF-8';
    const headers = { 'Content-Type': contentType };
    // const headers = secure ? {
    //     'Content-Type': contentType,
    //     // Authorization: 'Bearer ' + token
    // } : { 'Content-Type': contentType };

    const transactionUniqueId = store.state.permissions.transactionId;
    if (transactionUniqueId !== '') {
        headers.transactionUniqueId = transactionUniqueId;
    }
    const instance = Axios.create({
        baseURL: baseURL ? process.env.VUE_APP_CONTEXT_PATH : process.env.VUE_APP_HOST,
        headers: headers,
        timeout: 180000,
        withCredentials: false,
    });
    // Add a request interceptor
    instance.interceptors.request.use(
        config => {
            removePending(config); // 在請求開始前，對之前的請求做檢查取消操作
            repeatRequestCancel && addPending(config); // 將當前請求添加到 pending 中
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    // 攔截器攔截後端系統response操作
    instance.interceptors.response.use(
        response => {
            removePending(response); // 在請求結束後，移除本次請求
            const res = response.data;

            if (res.success || +response.status === 200) {
                return res;
            } else {
                return Promise.reject(res);
            }
        },
        error => {
            return Promise.reject(error);
        }
    );

    // 請求之前loading
    return new Promise((resolve, reject) => {
        let data = {};
        // get請求使用params字段
        if (method === 'get') data = { params };
        // post請求使用data字段
        if (method === 'post') data = { data: params };
        instance({
            url,
            method,
            ...data,
            responseType
        }).then((res) => {
            if (res.success || res.type === 'application/pdf') {
                resolve(res);
            } else if (!res.success && res.data?.redirectUrl) {
                window.location.href = res.data.redirectUrl;
                reject(res);
            } else {
                if (!closeAlert) {
                    // details 有的話
                    if (res.data?.details?.length === 0 || !res.data?.details) {
                        httpErrorStatusHandle(res, apiRoutePage);
                    } else {
                        errorDialog && httpErrorStatusHandle(res, apiRoutePage);
                    }
                }
                // errorDialog && httpErrorStatusHandle(res);
                reject(res);
            }
        }).catch((error) => {
            reject(error);
            if (!closeAlert) {
                errorDialog && httpErrorStatusHandle(error, apiRoutePage);
            }
        });
        // .finally(() => {
        //     console.log('finally');
        // });
    });
}
// GET
export function get(url, params, options) {
    return request(url, params, options, 'get');
}
// POST
export function post(url, params, options) {
    return request(url, params, options, 'post');
}
